import { FloatingFocusManager, FloatingPortal } from "@floating-ui/react";
import { Transition } from "@headlessui/react";
import { ReactNode, useContext } from "react";
import { twMerge } from "tailwind-merge";
import { DropdownContext } from "./dropdown";
import { useModal } from "../modal/modalContext";

export interface DropdownPanelProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  children: ReactNode | (({ close }: { close: () => void }) => ReactNode);
  focusOnToggle?: boolean;
  matchReferenceWidth?: boolean;
  modal?: boolean;
}

export const DropdownPanel = (props: Readonly<DropdownPanelProps>) => {
  const { portalRoot } = useModal();
  const {
    className,
    children,
    focusOnToggle = true,
    matchReferenceWidth = false,
    modal = true,
    ...rest
  } = props;
  const {
    isOpen: open,
    close,
    floatingRefs,
    floatingStyles,
    floatingContext,
    getFloatingProps,
  } = useContext(DropdownContext);

  const renderChildren = () =>
    typeof children === "function" ? children({ close }) : children;

  return (
    <FloatingPortal root={portalRoot}>
      <Transition
        show={open}
        enter="ease-out duration-300"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        className="absolute z-20"
      >
        <FloatingFocusManager
          context={floatingContext!}
          modal={modal}
          initialFocus={focusOnToggle ? undefined : -1}
        >
          <div
            {...rest}
            ref={floatingRefs!.setFloating}
            style={floatingStyles}
            className={twMerge(
              "bg-white py-2 border rounded-sm border-gunmetal-200 ring-inherit/5 overflow-auto shadow-lg z-20 focus-visible:outline-hidden",
              className
            )}
            {...getFloatingProps()}
          >
            {renderChildren()}
          </div>
        </FloatingFocusManager>
      </Transition>
    </FloatingPortal>
  );
};

export default DropdownPanel;
